import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled/macro';
import { DateTime } from 'luxon';
import { fetchRecentComments } from '../../store/api';
import {
  receiveRecentComments,
  markRecentRead,
} from '../../store/comments/commentsActions';
import { getRecentComments, getLastReadAt } from '../../store/comments/commentsSelectors';
import {
  toggleCommentsSidebar,
  setObservationCommentsOpen,
} from '../../store/ui/uiActions';
import { ReactComponent as IconClose } from '../../svgs/icon-close.svg';

const Panel = styled.aside`
  position: fixed;
  top: var(--top-bar);
  right: 0;
  height: calc(var(--app-top-height) - var(--top-bar));
  width: 24rem;
  max-width: 100vw;
  background-color: var(--surface-1);
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.2);
  clip-path: inset(0 0 0 -10px);
  display: flex;
  flex-direction: column;
  transform: translateX(${({ open }) => (open ? '0%' : '100%')});
  transition: transform 0.3s ease;
  z-index: 1001;
  pointer-events: ${({ open }) => (open ? 'auto' : 'none')};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background-color: var(--color-dark);
  font-weight: bold;
  font-size: 1rem;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  background: none;
  cursor: pointer;
  padding: 4px;
  display: inline-flex;
  align-items: center;
  & svg {
    width: 14px;
    height: 14px;
    fill: var(--color-white);
  }
  & svg path {
    fill: var(--color-white);
  }
`;

const ListBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Empty = styled.div`
  color: var(--color-charcoal-light);
  font-style: italic;
  font-size: 0.85rem;
  padding: 24px 0;
  text-align: center;
`;

const Item = styled.button`
  position: relative;
  text-align: left;
  display: flex;
  flex-direction: column;
  padding: 0;
  background-color: var(--color-dark);
  border: 1px solid var(--divider, rgba(255, 255, 255, 0.06));
  border-left: 3px solid ${(props) => {
    switch (props.type) {
      case 'action_taken':
        return 'var(--color-green)';
      case 'important':
        return 'var(--color-observations)';
      case 'unimportant':
        return 'var(--color-charcoal-light)';
      default:
        return 'var(--color-charcoal)';
    }
  }};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
  &:hover {
    border-color: var(--color-charcoal-light);
  }
  &:hover > div:last-of-type {
    background-color: var(--color-darkest);
  }
  &:active {
    transform: translateY(1px);
  }
`;

const ItemHeader = styled.div`
  background-color: var(--color-dark-rgba);
  padding: 8px 12px;
  border-bottom: 1px solid var(--divider, rgba(255, 255, 255, 0.06));
`;

const ItemContent = styled.div`
  padding: 10px 12px 12px;
  background-color: var(--color-dark);
  display: flex;
  flex-direction: column;
  transition: background-color 0.15s ease;
  ${(props) => props['data-unread'] && 'background-color: var(--color-dark-shade);'}
`;

const Label = styled.div`
  font-size: 0.85rem;
  color: var(--color-white);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.7rem;
  color: var(--color-charcoal-light);
  margin-bottom: 6px;
  flex-wrap: wrap;
`;

const Author = styled.span`
  font-weight: 600;
  color: ${(props) => (props.role === 'automation' ? 'var(--color-green)' : 'var(--color-white)')};
`;

const Dot = styled.span`
  color: var(--color-charcoal-light);
  font-size: 0.7rem;
`;

const TypeTag = styled.span`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 0.6rem;
  text-transform: uppercase;
  font-weight: bold;
  letter-spacing: 0.02em;
  background-color: ${(props) => {
    switch (props.type) {
      case 'action_taken':
        return 'var(--color-green)';
      case 'important':
        return 'var(--color-observations)';
      case 'unimportant':
        return 'var(--color-charcoal-light)';
      default:
        return 'var(--color-charcoal)';
    }
  }};
  color: ${(props) =>
    props.type === 'action_taken' || props.type === 'important'
      ? 'var(--color-charcoal)'
      : 'var(--color-white)'};
`;

const Body = styled.div`
  font-size: 0.8rem;
  color: var(--color-gray);
  white-space: pre-wrap;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const UnreadDot = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--color-observations);
  z-index: 1;
`;


const formatTime = (iso) => {
  const dt = DateTime.fromISO(iso);
  const diff = DateTime.utc().diff(dt, ['minutes', 'hours', 'days']).toObject();
  if (diff.days >= 1) return dt.toFormat('LLL d, HH:mm');
  if (diff.hours >= 1) return `${Math.floor(diff.hours)}h ago`;
  if (diff.minutes >= 1) return `${Math.floor(diff.minutes)}m ago`;
  return 'just now';
};

const CommentsSidebar = () => {
  const dispatch = useDispatch();
  const open = useSelector((state) => state.ui.uiCommentsSidebarOpen);
  const comments = useSelector(getRecentComments);
  const lastReadAt = useSelector(getLastReadAt);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchRecentComments({ days: 7, limit: 100 })
      .then(({ response }) => {
        if (cancelled || !response) return;
        const list = response.comments || [];
        dispatch(receiveRecentComments({ comments: list }));
        if (list.length > 0) {
          // Advance the read cursor to the newest loaded comment rather than the
          // client clock — clock skew would otherwise leave viewed comments
          // permanently "unread" (or pre-mark not-yet-seen ones read).
          const newest = list.reduce(
            (max, c) => (max == null || c.createdAt > max ? c.createdAt : max),
            null
          );
          dispatch(markRecentRead({ timestamp: newest }));
        }
      })
      .catch(console.error);
    return () => {
      cancelled = true;
    };
  }, [open, dispatch]);

  const handleClose = () => dispatch(toggleCommentsSidebar(false));

  const handleItemClick = (functionId) => {
    const target = document.querySelector(`[data-id="${functionId}"]`);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    dispatch(setObservationCommentsOpen(functionId));
  };

  const isUnread = (comment) => {
    if (!lastReadAt) return true;
    return new Date(comment.createdAt).getTime() > new Date(lastReadAt).getTime();
  };

  return (
    <>
      <Panel open={open} aria-hidden={!open} data-comments-sidebar="true">
        <Header>
          <span>Observation Messages</span>
          <CloseButton onClick={handleClose} aria-label="close comments sidebar">
            <IconClose />
          </CloseButton>
        </Header>
        <ListBody>
          {comments.length === 0 ? (
            <Empty>No recent comments.</Empty>
          ) : (
            comments.map((c) => (
              <Item
                key={c.commentId}
                type={c.type}
                // Stop mousedown here so the open thread dock's document-level
                // outside-click handler doesn't close it before this click reopens
                // it for the selected function (matches CardCommentOverlay).
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => handleItemClick(c.functionId)}
              >
                {isUnread(c) && <UnreadDot aria-hidden="true" />}
                <ItemHeader>
                  <Label>{c.snapshotLabel || c.functionId}</Label>
                </ItemHeader>
                <ItemContent data-unread={isUnread(c)}>
                  <MetaRow>
                    <Author role={c.authorRole}>{c.author}</Author>
                    <Dot>•</Dot>
                    <span>{formatTime(c.createdAt)}</span>
                    {c.type && c.type !== 'note' && (
                      <TypeTag type={c.type}>{c.type.replace('_', ' ')}</TypeTag>
                    )}
                  </MetaRow>
                  {c.text && <Body>{c.text}</Body>}
                </ItemContent>
              </Item>
            ))
          )}
        </ListBody>
      </Panel>
    </>
  );
};

export default CommentsSidebar;
